import { useEffect, useState } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { NAV, BRAND, waLink } from "@/data/site";
import { scrollToId, WaIcon, ease, useLang } from "./common";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
          scrolled ? "bg-white/85 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(15,41,91,0.25)] py-2" : "bg-[#F8F9FA]/70 backdrop-blur-md py-4"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between gap-4">
          <button data-testid="header-logo" onClick={() => go("home")} className="shrink-0" aria-label="Vivan Energia home">
            <img src="/assets/logo-sm.png" alt="Vivan Energia" className="h-12 sm:h-14 w-auto" />
          </button>
          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((n, i) => (
              <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => go(n.id)} className="group relative px-3 py-2 text-[13px] font-semibold text-[#0F295B]">
                {t.nav[i]}
                <span className="absolute left-3 right-3 bottom-1 h-[2px] bg-[#00A651] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "en" ? "hi" : "en")}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#0F295B]/20 px-3 py-2 text-sm font-bold text-[#0F295B] hover:bg-[#0F295B] hover:text-white transition-colors"
              aria-label="Switch language"
            >
              <Languages className="w-4 h-4" /> {t.otherLang}
            </button>
            <a href={`tel:${BRAND.phones[0].replace(/\s/g, "")}`} data-testid="header-call-now"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#0F295B]/20 px-4 py-2.5 text-sm font-bold text-[#0F295B] hover:bg-[#0F295B] hover:text-white transition-colors">
              <Phone className="w-4 h-4" /> {t.callNow}
            </a>
            <a href={`tel:${BRAND.phones[0].replace(/\s/g, "")}`} data-testid="header-call-icon" aria-label="Call now"
              className="sm:hidden w-10 h-10 grid place-items-center rounded-full border border-[#0F295B]/20 text-[#0F295B]">
              <Phone className="w-4 h-4" />
            </a>
            <motion.a whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} href={waLink()} target="_blank" rel="noreferrer" data-testid="header-site-visit"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#00A651] px-5 py-2.5 text-sm font-bold text-white shadow-[0_10px_24px_-10px_rgba(0,166,81,0.8)] hover:bg-[#008842] transition-colors">
              <WaIcon className="w-4 h-4" /> {t.siteVisit}
            </motion.a>
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(true)} className="xl:hidden w-10 h-10 grid place-items-center rounded-full bg-[#0F295B] text-white" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
        <motion.div style={{ scaleX: progress }} className="absolute bottom-0 left-0 right-0 h-[3px] origin-left bg-[#F39C12]" />
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} go={go} />}</AnimatePresence>
    </>
  );
};

const MobileMenu = ({ onClose, go }) => {
  const { t, lang, setLang } = useLang();
  return (
    <motion.div
      data-testid="mobile-menu"
      initial={{ clipPath: "circle(0% at 100% 0%)" }}
      animate={{ clipPath: "circle(150% at 100% 0%)" }}
      exit={{ clipPath: "circle(0% at 100% 0%)" }}
      transition={{ duration: 0.7, ease }}
      className="fixed inset-0 z-[60] bg-[#0F295B] text-white flex flex-col px-6 pt-6 pb-10"
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-mono2 text-xs tracking-[0.25em] text-[#F39C12]">{t.menu}</span>
          <button onClick={() => setLang(lang === "en" ? "hi" : "en")} data-testid="mobile-lang-toggle"
            className="rounded-full border border-white/30 px-3 py-1 text-xs font-bold flex items-center gap-1.5">
            <Languages className="w-3.5 h-3.5" /> {t.otherLang}
          </button>
        </div>
        <button data-testid="mobile-menu-close" onClick={onClose} className="w-11 h-11 grid place-items-center rounded-full bg-white/10" aria-label={t.close}>
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="mt-10 flex flex-col gap-1">
        {NAV.map((n, i) => (
          <motion.button key={n.id} data-testid={`mobile-nav-${n.id}`}
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.06, ease }}
            onClick={() => go(n.id)} className="flex items-center justify-between border-b border-white/10 py-4 text-left font-display text-2xl">
            {t.nav[i]} <ArrowUpRight className="w-5 h-5 text-[#00A651]" />
          </motion.button>
        ))}
      </div>
      <a href={waLink()} target="_blank" rel="noreferrer" data-testid="mobile-menu-whatsapp" className="mt-auto inline-flex justify-center items-center gap-2 rounded-full bg-[#00A651] py-4 font-bold">
        <WaIcon /> {t.siteVisit}
      </a>
    </motion.div>
  );
};
